import type { ReactNode } from 'react'
import { parseWhatsAppText, type Inline } from '../lib/whatsappText'

function renderInline(nodes: Inline[], keyPrefix = ''): ReactNode[] {
  return nodes.map((n, i) => {
    const key = `${keyPrefix}${i}`
    if (n.t === 'text') return <span key={key}>{n.v}</span>
    if (n.t === 'code') {
      return (
        <code key={key} className="rounded bg-black/10 px-1 font-mono text-[0.92em]">
          {n.v}
        </code>
      )
    }
    const inner = renderInline(n.c, `${key}-`)
    if (n.t === 'b') return <strong key={key} className="font-semibold">{inner}</strong>
    if (n.t === 'i') return <em key={key}>{inner}</em>
    return <s key={key}>{inner}</s>
  })
}

/**
 * Renders WhatsApp-formatted text. The caller keeps `whitespace-pre-wrap` on the
 * container so plain newlines survive; lists and quotes reset it themselves.
 */
export function WhatsAppText({ text }: { text: string }) {
  const blocks = parseWhatsAppText(text)

  return (
    <>
      {blocks.map((b, i) => {
        const key = `b${i}`
        if (b.t === 'pre') {
          return (
            <pre key={key} className="my-1 overflow-x-auto rounded bg-black/10 p-2 font-mono text-[0.92em] whitespace-pre-wrap break-words">
              {b.v}
            </pre>
          )
        }
        if (b.t === 'quote') {
          return (
            <blockquote key={key} className="my-1 border-l-4 border-black/20 pl-2 text-current/80">
              {renderInline(b.c, `${key}-`)}
            </blockquote>
          )
        }
        if (b.t === 'ul') {
          return (
            <ul key={key} className="my-1 list-disc whitespace-normal pl-5">
              {b.items.map((it, j) => <li key={`${key}-${j}`}>{renderInline(it, `${key}-${j}-`)}</li>)}
            </ul>
          )
        }
        if (b.t === 'ol') {
          return (
            <ol key={key} start={b.start} className="my-1 list-decimal whitespace-normal pl-5">
              {b.items.map((it, j) => <li key={`${key}-${j}`}>{renderInline(it, `${key}-${j}-`)}</li>)}
            </ol>
          )
        }
        return <span key={key}>{i > 0 && '\n'}{renderInline(b.c, `${key}-`)}</span>
      })}
    </>
  )
}
